import {
  INSURANCE_INFORMATION_MAX_ENTRIES,
  INSURANCE_INFORMATION_MAX_ENTRY_CHARACTERS,
  type AssistantBehavior,
} from './assistant-settings-contracts'

export const CARRIERS_MAX = 100
export const CARRIER_NAME_MAX = 120
export const CARRIER_ALIASES_MAX = 12
export const CARRIER_ALIAS_MAX = 80

export type Carrier = { name: string; aliases: string[] }
export type InsuranceDraft = {
  proactive: boolean
  carriers: Carrier[]
  notes: string[]
}

/**
 * How the insurance matcher compares names: case, accents and everything but
 * letters and digits are ignored, so "Met Life" and "MetLife" are the same.
 */
export function carrierKey(value: string) {
  return value
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]/gu, '')
}

export function insuranceFromBehavior(
  behavior: AssistantBehavior
): InsuranceDraft {
  return {
    proactive:
      behavior.draftIntakePreferences?.proactiveInsuranceQuestioning ?? true,
    carriers: (behavior.acceptedInsurances?.carriers ?? []).map((carrier) => ({
      name: carrier.name,
      aliases: [...carrier.aliases],
    })),
    notes: [...behavior.insuranceInformation],
  }
}

/**
 * The saved behavior with only the insurance changes applied. Unchanged parts
 * are sent exactly as stored (an omitted preference stays omitted, a null
 * carrier list stays null), and every other field comes from `canonical`.
 */
export function insurancePayload(
  canonical: AssistantBehavior,
  draft: InsuranceDraft,
  assistantName: string
): AssistantBehavior {
  const stored = insuranceFromBehavior(canonical)
  const same = (left: unknown, right: unknown) =>
    JSON.stringify(left) === JSON.stringify(right)
  return {
    ...canonical,
    aiName: assistantName,
    ...(draft.proactive === stored.proactive
      ? {}
      : {
          // The canonical object requires both booleans; the age preference
          // keeps its stored (or default ON) value.
          draftIntakePreferences: {
            proactiveAgeQuestioning:
              canonical.draftIntakePreferences?.proactiveAgeQuestioning ?? true,
            proactiveInsuranceQuestioning: draft.proactive,
          },
        }),
    insuranceInformation: same(draft.notes, stored.notes)
      ? canonical.insuranceInformation
      : draft.notes,
    acceptedInsurances: same(draft.carriers, stored.carriers)
      ? canonical.acceptedInsurances
      : { schemaVersion: 1, carriers: draft.carriers },
  }
}

/** Why this carrier can't be applied, if it can't. */
export function carrierError(
  carrier: Carrier,
  others: Carrier[]
): { name?: string; aliases?: string } {
  const name = carrier.name.trim()
  const errors: { name?: string; aliases?: string } = {}
  if (!name) errors.name = 'Enter the carrier’s name.'
  else if (name.length > CARRIER_NAME_MAX)
    errors.name = `Use ${CARRIER_NAME_MAX} characters or fewer.`
  else if (!carrierKey(name))
    errors.name = 'Use letters or numbers in the name.'
  else {
    const twin = others.find(
      (other) => carrierKey(other.name) === carrierKey(name)
    )
    if (twin) errors.name = `${twin.name} is already in the list.`
  }
  if (carrier.aliases.length > CARRIER_ALIASES_MAX)
    errors.aliases = `Add up to ${CARRIER_ALIASES_MAX} other names.`
  return errors
}

/** Why this other name can't be added to the carrier, if it can't. */
export function aliasError(alias: string, carrier: Carrier) {
  const value = alias.trim()
  if (!value) return null
  if (value.length > CARRIER_ALIAS_MAX)
    return `Use ${CARRIER_ALIAS_MAX} characters or fewer.`
  if (!carrierKey(value)) return 'Use letters or numbers.'
  if (carrierKey(value) === carrierKey(carrier.name))
    return 'That’s already the carrier’s name.'
  if (carrier.aliases.some((item) => carrierKey(item) === carrierKey(value)))
    return 'Already added.'
  if (carrier.aliases.length >= CARRIER_ALIASES_MAX)
    return `Add up to ${CARRIER_ALIASES_MAX} other names.`
  return null
}

/**
 * Other carriers that already answer to one of this carrier's names. The
 * matcher can't tell them apart, so a patient saying it matches neither.
 */
export function sharedNames(carrier: Carrier, others: Carrier[]) {
  const mine = new Set(
    [carrier.name, ...carrier.aliases].map(carrierKey).filter(Boolean)
  )
  return others
    .filter((other) =>
      [other.name, ...other.aliases].some((value) =>
        mine.has(carrierKey(value))
      )
    )
    .map((other) => other.name)
}

export function noteError(note: string, others: string[]) {
  const value = note.trim()
  if (!value) return 'Enter the note.'
  if (value.length > INSURANCE_INFORMATION_MAX_ENTRY_CHARACTERS)
    return `Use ${INSURANCE_INFORMATION_MAX_ENTRY_CHARACTERS} characters or fewer.`
  if (others.includes(value)) return 'This note is already in the list.'
  return null
}

export function insuranceProblem(draft: InsuranceDraft) {
  if (draft.carriers.length > CARRIERS_MAX)
    return `list up to ${CARRIERS_MAX} carriers to save`
  if (draft.notes.length > INSURANCE_INFORMATION_MAX_ENTRIES)
    return `keep up to ${INSURANCE_INFORMATION_MAX_ENTRIES} notes to save`
  return null
}

export function matchesSearch(carrier: Carrier, query: string) {
  const key = carrierKey(query)
  if (!key) return true
  return [carrier.name, ...carrier.aliases].some((value) =>
    carrierKey(value).includes(key)
  )
}
